import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useUserStore from '@/store/useUserStore';
import httpClient from '../httpClient';
import { PeriodClosure, PeriodClosurePayload } from './periodClosure.model';

const PERIOD_CLOSURE_BASE_PATH = '/period-closure';
const PERIOD_CLOSURE_KEY = 'period-closure';

// Get latest period closure
export const usePeriodClosure = () => {
  const token = useUserStore((state) => state.token);

  return useQuery<PeriodClosure | null>({
    queryKey: [PERIOD_CLOSURE_KEY],
    enabled: Boolean(token),
    queryFn: async (): Promise<PeriodClosure | null> => {
      try {
        const response = await httpClient.get<PeriodClosure | null>(
          `${PERIOD_CLOSURE_BASE_PATH}/latest`,
        );
        return response.data ?? null;
      } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
          const err = error as { response?: { status?: number } };
          if (err.response?.status === 204) return null;
        }
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Create/Update period closure
export const useUpdatePeriodClosure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PeriodClosurePayload) => {
      const response = await httpClient.post(PERIOD_CLOSURE_BASE_PATH, { data: payload });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERIOD_CLOSURE_KEY] });
    },
  });
};

// Delete period closure (open period)
export const useDeletePeriodClosure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await httpClient.delete(PERIOD_CLOSURE_BASE_PATH);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERIOD_CLOSURE_KEY] });
    },
  });
};

// Helper to parse date string without timezone conversion
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper function to check if a date is in closed period
export const isDateInClosedPeriod = (
  date: Date | null | undefined,
  closedPeriod: PeriodClosure | null,
): boolean => {
  if (!date || !closedPeriod) return false;
  const closedDate = parseLocalDate(closedPeriod.closedUntilDate);
  return date <= closedDate;
};
